"use client";
import { useState, useEffect } from 'react';

import Icon from '@/components/ui/AppIcon';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Detect active section
      const sections = ['home', 'about', 'services', 'contact'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  const navLinks = [
    { id: 'nav_home', label: 'الرئيسية', labelEn: 'Home', href: 'home' },
    { id: 'nav_about', label: 'من نحن', labelEn: 'About', href: 'about' },
    { id: 'nav_services', label: 'خدماتنا', labelEn: 'Services', href: 'services' },
    { id: 'nav_contact', label: 'تواصل معنا', labelEn: 'Contact', href: 'contact' },
  ];

  return (
    <nav
      className={`fixed w-full z-50 top-0 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm' :'bg-white/80 backdrop-blur-md'
      }`}
      dir="rtl"
    >
      <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => scrollToSection('home')}
          className="flex items-center gap-4 group cursor-pointer"
        >
          {/* Geometric Logo Mark */}
          <div className="flex flex-col items-start gap-[3px] py-1">
            <div className="w-3 h-[1.5px] bg-primary rounded-full transition-all duration-500 group-hover:w-5"></div>
            <div className="w-2 h-[1.5px] bg-primary/70 rounded-full transition-all duration-500 delay-75 group-hover:w-5"></div>
            <div className="w-4 h-[1.5px] bg-primary rounded-full transition-all duration-500 delay-100"></div>
          </div>
          
          {/* Company Name */}
          <div className="flex flex-col justify-center">
            <span className="font-cairo text-xs leading-none tracking-[0.15em] font-semibold text-slate-800">
              الأنيق المتميز
            </span>
            <span className="font-cairo text-[10px] leading-none tracking-[0.1em] font-normal text-slate-600 mt-1">
              استيراد وسائل النقل
            </span>
          </div>
        </button>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8 text-sm font-medium tracking-wide text-slate-600">
          {navLinks?.map((link) => (
            <button
              key={link?.id}
              onClick={() => scrollToSection(link?.href)}
              className={`font-cairo transition-colors uppercase tracking-widest hover:text-primary ${
                activeSection === link?.href ? 'text-primary' : ''
              }`}
            >
              {link?.label}
            </button>
          ))}
          
          {/* CTA Button */}
          <a
            href="tel:+218918000071"
            className="px-6 py-2.5 border border-primary/30 bg-primary/10 text-primary rounded-lg hover:bg-primary hover:text-white transition-all duration-300 font-cairo font-medium"
          >
            اتصل بنا
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden text-slate-600 hover:text-slate-800 transition-colors"
          aria-label="Toggle menu"
        >
          <Icon name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
        </button>
      </div>
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks?.map((link) => (
              <button
                key={link?.id}
                onClick={() => scrollToSection(link?.href)}
                className={`font-cairo text-sm font-medium transition-colors hover:text-primary text-right ${
                  activeSection === link?.href ? 'text-primary' : 'text-slate-600'
                }`}
              >
                {link?.label}
              </button>
            ))}
            
            <a
              href="tel:+218918000071"
              className="px-6 py-3 border border-primary/30 bg-primary/10 text-primary rounded-lg hover:bg-primary hover:text-white transition-all duration-300 font-cairo font-medium text-center"
            >
              اتصل بنا
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}